import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, ArrowUpRight, CalendarHeart } from "lucide-react";
import Header from "@/components/Header";
import EventCard from "@/components/EventCard";
import CategoryChips from "@/components/CategoryChips";
import { categoryApi, eventApi } from "@/lib/api";
import { setSEO } from "@/lib/seo";
import { useApp } from "@/context/AppContext";

export default function Home() {
  const { selectedCity, cities } = useApp();
  const [params] = useSearchParams();
  const search = params.get("search") || "";

  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [weekend, setWeekend] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    categoryApi.list({ enabled: true }).then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { city: selectedCity };
    if (active !== "all") p.category = active;
    if (search) p.search = search;
    if (weekend) p.weekend = true;
    eventApi.list(p).then((data) => setEvents(data)).finally(() => setLoading(false));
  }, [selectedCity, active, search, weekend]);

  const featured = useMemo(() => {
    const sponsored = events.find((e) => e.is_sponsored);
    if (sponsored) return sponsored;
    return events.find((e) => e.featured) || events[0];
  }, [events]);

  const cityLabel = cities.find((c) => c.key === selectedCity)?.name || "your city";

  // Dynamic SEO for the home / city landing
  useEffect(() => {
    const title = `Fanpit.Club — Discover the best events${cityLabel && cityLabel !== "your city" ? " in " + cityLabel : ""}`;
    const description = `Curated events, workshops, comedy nights, gigs and food festivals${cityLabel && cityLabel !== "your city" ? " in " + cityLabel : ""}. Hand-picked so your evenings never feel ordinary.`;
    const keywords = [
      "events", cityLabel, "concerts", "workshops", "comedy", "nightlife",
      "food festivals", "kids events", "Fanpit", "Fanpit.Club",
    ].filter(Boolean).join(", ");
    const image = events[0]?.image_url || "";
    const url = typeof window !== "undefined" ? window.location.href : "";
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Fanpit.Club",
      url: typeof window !== "undefined" ? window.location.origin : "https://fanpit.club",
      potentialAction: {
        "@type": "SearchAction",
        target: `${typeof window !== "undefined" ? window.location.origin : ""}/?search={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    setSEO({ title, description, keywords, image, url, type: "website", jsonLd });
  }, [cityLabel, events]);

  return (
    <div className="grain relative min-h-screen">
      <Header />

      <main className="pt-24 md:pt-28 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-[#FA003F] mb-6">
                <Sparkles className="h-3.5 w-3.5" /> Curated events near you
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.95] tracking-tighter">
                What's happening in{" "}
                <span className="text-[#FA003F] italic">{cityLabel}</span>
                <br />
                <span className="text-[#1A1A1A]/70">this week?</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-2xl">
                From rooftop gigs to pottery workshops — discover, book and go. Every event here is
                hand-picked so your evenings never feel ordinary.
              </p>
            </div>

            {featured && (
              <a
                href={`/event/${featured.id}`}
                className="lg:col-span-4 group relative rounded-3xl overflow-hidden aspect-[3/4] block border border-black/10"
                data-testid="hero-featured-event"
              >
                <img
                  src={featured.image_url}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                  <div className="text-[11px] font-bold tracking-widest uppercase text-[#FA003F] mb-2 inline-flex items-center gap-1.5">
                    {featured.is_sponsored ? <><Sparkles className="h-3 w-3" /> Sponsored spotlight</> : "Featured Event"}
                  </div>
                  <div className="font-display font-black text-2xl leading-tight line-clamp-3">
                    {featured.title}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold self-start bg-white text-black rounded-full px-3.5 py-1.5">
                    See details <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </a>
            )}
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14">
          <CategoryChips categories={categories} active={active} onChange={setActive} />
        </section>

        {/* Event grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
              {search ? `Results for "${search}"` : weekend ? "This weekend in " + cityLabel : "Upcoming in " + cityLabel}
            </h2>
            <div className="flex items-center gap-3">
              <button
                data-testid="weekend-toggle"
                onClick={() => setWeekend((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-full h-10 px-4 text-sm font-bold border transition-all ${
                  weekend
                    ? "bg-[#FA003F] text-white border-transparent shadow-[0_8px_24px_rgba(250,0,63,0.35)]"
                    : "bg-white text-[#1A1A1A] border-black/10 hover:border-[#FA003F]/40 hover:bg-[#FA003F]/5"
                }`}
              >
                <CalendarHeart className="h-4 w-4" />
                This Weekend
              </button>
              <div className="text-sm text-gray-500 hidden sm:block">
                {events.length} events
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-white border border-black/10 p-3 animate-pulse">
                  <div className="aspect-[4/3] rounded-xl bg-gray-100" />
                  <div className="h-4 bg-gray-100 mt-4 rounded" />
                  <div className="h-4 bg-gray-100 mt-2 w-2/3 rounded" />
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div
              data-testid="empty-events-state"
              className="bg-white border border-dashed border-black/20 rounded-3xl p-16 text-center"
            >
              <div className="text-2xl font-display font-black mb-2">Nothing here yet.</div>
              <p className="text-gray-500">
                {weekend ? "No events this weekend — try turning off the weekend filter." : "Try a different city or category to find something exciting."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-black/10 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="font-display font-black text-lg text-[#1A1A1A]">Fanp<span className="text-[#FA003F]">i</span>t.Cl<span className="text-[#FA003F]">u</span>b</div>
          <div>© {new Date().getFullYear()} Fanpit.Club. Every evening deserves a plan.</div>
        </div>
      </footer>
    </div>
  );
}
